// Eskom PARCC — horizontal stacked category bar (recharts).
//
// Two-segment sibling of {@link CategoryBar}: an "open" segment (per-bar colour,
// highlighted) stacked with a muted "closed" segment on the same track. Used by
// the Regulatory-breaches card so each framework bar reconciles with the Gaps-open
// tile (open portion) while still showing the historical total. Pure presentational.
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell, LabelList } from 'recharts';
import { ChartFrame } from './ChartFrame';

export interface StackedCategoryDatum {
  label: string;
  /** Highlighted segment (Open / In Progress lines). */
  open: number;
  /** Muted segment (Closed / self-declared / unrecorded lines). */
  closed: number;
  /** Colour of the open segment. */
  colour: string;
}

export function StackedCategoryBar({
  data,
  axisColour,
  gridColour,
  closedColour,
  height = 200,
}: {
  data: StackedCategoryDatum[];
  axisColour: string;
  gridColour: string;
  /** Fill for the muted closed segment. */
  closedColour: string;
  height?: number;
}): React.JSX.Element {
  const withTotal = data.map((d) => ({ ...d, total: d.open + d.closed }));
  return (
    <div style={{ width: '100%', height }}>
      <ChartFrame>
        {({ width, height: h }) => (
          <BarChart
            width={width}
            height={h}
            data={withTotal}
            layout="vertical"
            margin={{ top: 4, right: 44, bottom: 4, left: 8 }}
          >
            <XAxis type="number" hide />
            <YAxis
              type="category"
              dataKey="label"
              width={132}
              tickLine={false}
              axisLine={false}
              tick={{ fill: axisColour, fontSize: 12 }}
            />
            <Tooltip
              cursor={{ fill: gridColour }}
              formatter={(value, name) => [Number(value).toLocaleString('en-ZA'), name === 'open' ? 'Open' : 'Closed']}
            />
            <Bar dataKey="open" stackId="s" radius={[4, 0, 0, 4]} barSize={16}>
              {withTotal.map((d) => (
                <Cell key={d.label} fill={d.colour} />
              ))}
            </Bar>
            <Bar dataKey="closed" stackId="s" radius={[0, 4, 4, 0]} barSize={16} fill={closedColour}>
              <LabelList
                dataKey="total"
                position="right"
                formatter={(value) => Number(value).toLocaleString('en-ZA')}
                style={{ fill: axisColour, fontSize: 12, fontWeight: 600 }}
              />
            </Bar>
          </BarChart>
        )}
      </ChartFrame>
    </div>
  );
}
